import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import {
  checkNullOrUndefined,
  displayName,
  formatDateString,
  formatter,
  isStaffWithoutPriceView,
} from "app/main/apps/common/AuraFunctions";
import NoItemMessage from "app/main/apps/common/NoItemMessage";
import AuraToolbarButton from "app/main/apps/common/AuraToolbarButton";
import HistorySplitView from "app/main/apps/common/HistorySplitView";
import { SimpleCardDocumentsViewer } from "app/main/apps/common/ImageRenderer";
import { documentsForRevisionHistory } from "../Vas/proofDocumentVisibility";
import { withBinaryFrom } from "./changeHistoryCompare";
import { getRevisionHistory, getRevisionRounds } from "../../store/orderSlice";
// PGSS-5359: the VAS-side visibility rule and the type sets it reads.
import {
  PRODUCT_REVISION_TYPES,
  VAS_REVISION_TYPES,
  canShowVasRevisions,
} from "./vasRevisionVisibility";

const isPendingStatus = (status) => {
  if (!status) return false;
  const s = String(status).toLowerCase();
  return (
    s.includes("awaiting") ||
    s.includes("pending") ||
    s.includes("out for")
  );
};

const statusPillSx = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("approv") && !s.includes("await")) {
    return { bg: "#e8f5ee", fg: "#15803d", border: "#cbe3d5" };
  }
  if (s.includes("reject")) {
    return { bg: "#fdecea", fg: "#b0524b", border: "#e0b4b0" };
  }
  if (isPendingStatus(status)) {
    return { bg: "#fdf3e3", fg: "#a96a14", border: "#f0dcb4" };
  }
  return { bg: "#f4f6f9", fg: "#475467", border: "#e6e9ef" };
};

const StatusPill = ({ status }) => {
  if (!status) return null;
  const colors = statusPillSx(status);
  return (
    <Chip
      size="small"
      label={status}
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 700,
        backgroundColor: colors.bg,
        color: colors.fg,
        border: `1px solid ${colors.border}`,
        textTransform: "capitalize",
      }}
    />
  );
};

const changeCountLabel = (data) => {
  const n = data?.order_detail_history?.length || 0;
  if (n === 0) return "No line snapshot";
  return `${n} line${n === 1 ? "" : "s"}`;
};

const truncateNotes = (notes, max = 120) => {
  const text = String(notes || "").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}…`;
};

/**
 * Collapsed-card preview: prefer request notes; if the send had none but the round
 * closed with a decision note (common for staff "request revision"), show that instead
 * so the card does not read "No request notes" while a decision note exists.
 */
const collapsedNotesPreview = (data) => {
  const request = truncateNotes(data?.notes);
  if (request) {
    return { text: request, kind: "request", title: data.notes };
  }
  const decision = truncateNotes(data?.decision_note);
  if (decision) {
    return {
      text: decision,
      kind: "decision",
      title: data.decision_note,
    };
  }
  return { text: "No request notes", kind: "empty", title: undefined };
};

// Always include index so duplicate revision_number values cannot collide
// (same fix pattern as OrderChangeHistory / PGSS-5196).
const revisionKey = (type, data, index) =>
  `${type}-${data?.revision_number ?? "x"}-${index}`;

// --- PGSS-5030 approval-rounds endpoint mapping ---------------------------------------------

const DOC_GROUP_TITLES = {
  proof: "Proof documents",
  production: "Production documents",
  drawing_document: "CAD documents",
  revision: "Revision documents",
  product_proof: "Product proof documents",
};

const docGroupTitle = (key) =>
  DOC_GROUP_TITLES[key] ||
  `${String(key).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())} documents`;

/** Group flat round documents by their own category — no backend pre-bucketing. */
/**
 * PGSS-5278: build the legacy view's document groups.
 *
 * The legacy endpoint returns four typed buckets plus a catch-all `documents` list. That catch-all was
 * rendered as "Documents submitted by client", which asserts an author the data does not carry — staff
 * uploads were shown as the customer's. It is also broader than it looks: the backend only routes
 * `proof`, `production`, `product_proof` and the three synthetic CAD labels, so a document carrying its
 * real `drawing_document` type, or none at all, lands there too.
 *
 * So the catch-all is grouped by each document's own `image_customisation_type` — the same rule the
 * rounds view uses — instead of being given one blanket title. Groups are merged by key so a type that
 * arrives both ways renders once, and every title comes from DOC_GROUP_TITLES, so the two views cannot
 * drift apart.
 */
export const legacyDocumentGroups = (data) => {
  const typed = [
    { key: "drawing_document", docs: data?.drawing_documents },
    { key: "proof", docs: data?.proof_documents },
    { key: "production", docs: data?.production_documents },
    { key: "product_proof", docs: data?.product_proof_documents },
  ];
  const merged = new Map();
  [...groupDocuments(data?.documents), ...typed].forEach((group) => {
    if (!group.docs?.length) return;
    const existing = merged.get(group.key);
    if (existing) {
      existing.docs = [...existing.docs, ...group.docs];
      return;
    }
    merged.set(group.key, {
      key: group.key,
      title: docGroupTitle(group.key),
      docs: [...group.docs],
    });
  });
  return Array.from(merged.values());
};

const groupDocuments = (documents) => {
  if (!documents?.length) return [];
  const groups = new Map();
  documents.forEach((doc) => {
    const key = doc?.image_customisation_type || "other";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(doc);
  });
  return Array.from(groups.entries()).map(([key, docs]) => ({
    key,
    title: docGroupTitle(key),
    docs,
  }));
};

const APPROVAL_STATE_LABELS = {
  awaiting: "Awaiting approval",
  approved: "Approved",
  rejected: "Rejected",
  revision_requested: "Revision requested",
  approved_internal: "Approved (internal)",
  // PGSS-5266: sent, then replaced by a later send before the customer answered. Worded as an outcome
  // rather than "Superseded" so it cannot be misread as a decision — nobody decided anything here.
  superseded: "Replaced by a later revision",
};

/** PGSS-5266: the one round state that closes without anyone having decided anything. */
export const SUPERSEDED_STATE = "superseded";

/**
 * PGSS-5266: how a closed round's banner should read.
 *
 * <p>A superseded round closed because the system replaced it when a later revision was sent — nobody
 * decided anything. It still carries text in `decision_note` (the system's explanation of why it moved),
 * so the banner renders; heading it "Decision" and styling it as one asserted the opposite of what that
 * note says, and the heading is what gets read first.
 *
 * Matches on the raw state, never the display label, so rewording a label cannot silently restyle a round.
 *
 * <p>PGSS-5286: the outcome word comes from {@link roundStatusLabel} — the round's own state — for the
 * reason given there. The pill beside this heading reads the same helper, so the two cannot disagree.
 */
export const closureBanner = (data) => {
  const superseded = data?.approval_state === SUPERSEDED_STATE;
  if (superseded) {
    return {
      superseded: true,
      heading: "Replaced · no decision recorded",
      background: "#f4f6f9",
      border: "#e6e9ef",
      headingClass: "text-[#667085]",
      testId: "revision-superseded",
    };
  }
  const label = roundStatusLabel(data);
  const outcome = label
    ? String(label).replace(/^\w/, (c) => c.toUpperCase())
    : "Closed";
  return {
    superseded: false,
    heading: `Decision · ${outcome}`,
    background: "#fff8ef",
    border: "#f0e0c8",
    headingClass: "text-[#a96a14]",
    testId: "revision-decision",
  };
};

const formatApprovalState = (state) =>
  APPROVAL_STATE_LABELS[state] ||
  String(state || "").replace(/_/g, " ");

/**
 * PGSS-5286: how a round's own state should read, wherever this card states it — the pill on the nav row
 * and the card header, the Decision heading, and the pending fallback.
 *
 * The two feeds disagree about `approval_status`, and only one of them is describing the round. On
 * `/api/orders/{id}/revisions` it is the round's state already formatted (roundToCardData). On
 * `/api/getRevisionHistory` — the feed behind the VAS popup and the whole customer portal — it is the
 * ORDER's status as at that revision, which is a different fact: an approved round on an order still
 * sitting at "Waiting for VAS Proof Approval" would wear that as its own status, and once the decision
 * started arriving (PGSS-5286) it would have done so beside a "Decision · Approved" heading.
 *
 * So the state wins wherever it exists, and `approval_status` is the fallback for rows predating the
 * approval-round columns, which have no state to read. One rule, so the pill and the heading cannot
 * disagree with each other about the same round.
 */
export const roundStatusLabel = (data) =>
  data?.approval_state
    ? formatApprovalState(data.approval_state)
    : data?.approval_status;


/**
 * PGSS-5277: what a rendered list already establishes about its own contents — see vasRevisionIdsFor.
 * MIXED is the rounds endpoint (every type in one list), VAS and PRODUCT are the legacy endpoint's
 * server-side buckets.
 */
export const LIST_SCOPE_MIXED = "mixed";
export const LIST_SCOPE_VAS = "vas";
export const LIST_SCOPE_PRODUCT = "product";

/**
 * PGSS-5277: the per-order counter inside a revision number. `sp_create_order_detail_history` writes
 * `CONCAT('REV', LPAD(n, 4, '0'))`, so the digits are the order's revision sequence and the only ordering
 * that every screen agrees on. Null when there is nothing numeric to read.
 */
export const revisionSequence = (revisionNumber) => {
  const digits = String(revisionNumber ?? "").match(/\d+/);
  return digits ? Number(digits[0]) : null;
};

/**
 * PGSS-5277: the VAS list in revision order, newest first.
 *
 * The legacy feed is bucketed, and the VAS list is `proof` then `production` — each bucket descending on
 * its own, but the join between them is not. An order whose VAS work reached production therefore listed
 * REV0008, 0007, 0006, 0005 and only then REV0010, 0009. That is wrong on its own terms, and it is worse
 * next to a fix about the numbering being hard to follow: the ids would have read 04, 03, 02, 01, 06, 05
 * down the page. The rounds endpoint has no such join and is already ordered.
 *
 * Only reorders when every entry has a readable number: with nothing to rank a round by, leaving the
 * server's order alone beats inventing one.
 */
export const newestRevisionFirst = (items) => {
  const list = Array.isArray(items) ? items : [];
  if (!list.every((item) => revisionSequence(item?.revision_number) !== null)) {
    return list;
  }
  return [...list].sort(
    (a, b) => revisionSequence(b?.revision_number) - revisionSequence(a?.revision_number)
  );
};

/**
 * PGSS-5277: the `type` a RevisionSplitView renders under is exactly which feed and bucket it is showing —
 * "rounds" is the mixed rounds endpoint, "vas" and "product" are the legacy endpoint's buckets, and they
 * are the segment keys, so they cannot drift apart from the lists they name.
 */
export const listScopeForType = (type) => {
  if (type === "vas") return LIST_SCOPE_VAS;
  if (type === "product") return LIST_SCOPE_PRODUCT;
  return LIST_SCOPE_MIXED;
};

/**
 * PGSS-5277: where a round sits among the VAS rounds, for the VAS-only view.
 *
 * An order has ONE revision sequence and each round keeps its real number, so a VAS-only list that
 * begins at the order's fifth round opens on "REV0005" and reads as though four VAS revisions are
 * missing. Reference order 25240: quote REV0001-0002, drawing REV0003-0004, then proof REV0005-0008
 * and vas_final REV0009-0010 — six VAS rounds whose first is REV0005.
 *
 * This adds position alongside the number rather than replacing it. Renumbering the view 1..6 was the
 * other option and was rejected deliberately: the displayed number would stop matching
 * `order_detail_history.revision_number`, the Product view and the API, so "the customer mentioned
 * REV0002" would no longer identify one round. That display-vs-stored ambiguity cost real time on
 * PGSS-5286, where a popup number had to be ruled out as a possible remapping before the trace could
 * continue. Keeping the stored number authoritative everywhere is worth more than a tidier count.
 *
 * Counted among the VAS rounds *within the given list*, never from the list index, so the id is the
 * same in the customer's VAS-only view and in staff's mixed view. That is the whole point: a customer
 * quotes "VAS-01" and staff must land on the same round. An index-derived number would have been
 * right only where the list happens to be exactly the VAS rounds, and silently wrong elsewhere —
 * which is the worst failure available here, because both screens would look correct.
 *
 * ⚠️ THIS ID IS DERIVED, NOT STORED. Nothing in the database holds it; it is a count over rounds whose
 * `revision_type` is VAS. So it can move retroactively: there are legacy rows predating PGSS-5041 with
 * no revision_type, excluded from the count today, and typing any of them as VAS would shift every id
 * on that order. That is why staff always see it *alongside* `order_detail_history.revision_number`
 * rather than instead of it — the stored number stays the thing that cannot drift, and any dispute is
 * settled against it.
 *
 * Format chosen over REVAS0001 deliberately: "REVAS0001" sits two characters from "REV0001" mid-token,
 * so a misread is silent and both strings look valid. "VAS-01" cannot be mistaken for a REV number.
 *
 * `listScope` says what the list itself already establishes about its contents, because the two feeds
 * this screen has do not describe a round the same way:
 *
 *   LIST_SCOPE_MIXED   GET /api/orders/{id}/revisions — one list of every type, each round carrying its
 *                      own `revision_type`. Read the type; it is the only thing separating a VAS round
 *                      from a quote round here.
 *   LIST_SCOPE_VAS     GET /api/getRevisionHistory — already split server-side into proof / production /
 *                      product, and the VAS dialogs render `proof + production`. Membership of that list
 *                      IS the statement that these are the VAS rounds (OrderResponseMapper.revisionBucket,
 *                      which PGSS-5257 made reject quote/order/drawing outright). So a round counts unless
 *                      it is explicitly typed product.
 *   LIST_SCOPE_PRODUCT the same endpoint's `product` list — never issue an id here, even for a round the
 *                      bucket inference happened to type VAS.
 *
 * Reading `revision_type` alone was the PGSS-5277 re-open: the legacy payload never sent that key, so on
 * every screen it feeds — the VAS Revision Histories popup on the Proof and Production pages, and the
 * whole customer portal, which cannot call the staff-only rounds endpoint — no round matched, no id was
 * issued, and the list still opened on REV0005. Only the staff Revision Histories tab, on the rounds
 * endpoint, ever showed the fix. `revision_type` is now sent on the legacy shape too, so a typed round is
 * read from its type on either feed; the scope is what keeps untyped legacy rows from silently dropping
 * out of the count on the one list that is defined as holding them.
 *
 * Ranked by the stored revision number, not by where a round sits in the list, because the two feeds do
 * not agree on order either: the rounds endpoint is one sequence newest-first, but the VAS dialogs build
 * their list as `proof` followed by `production` (SubmitVas / ChooseSupplier), which on 25240 is
 * REV0008, 0007, 0006, 0005, then REV0010, 0009. Counting down that list hands VAS-01 to REV0009 and
 * VAS-03 to the REV0005 this ticket is about. `revision_number` is `REV` + a zero-padded per-order
 * counter (sp_create_order_detail_history), so its numeric part IS the sequence and gives the same
 * answer on any screen however that screen chose to order its rows.
 *
 * Falls back to newest-first list position only when a round carries no readable number — there is
 * nothing better to rank on then, and it is the behaviour every list with numbers already had.
 *
 * Returns a Map of list index -> id.
 */
export const vasRevisionIdsFor = (items, listScope = LIST_SCOPE_MIXED) => {
  const list = Array.isArray(items) ? items : [];
  const ids = new Map();
  if (listScope === LIST_SCOPE_PRODUCT) return ids;
  const isVasRound = (item) => {
    if (!item) return false;
    return listScope === LIST_SCOPE_VAS
      ? !PRODUCT_REVISION_TYPES.has(item.revision_type)
      : VAS_REVISION_TYPES.has(item.revision_type);
  };
  const vasIndexes = [];
  list.forEach((item, listIndex) => {
    if (isVasRound(item)) vasIndexes.push(listIndex);
  });
  const sequenceAt = (listIndex) => revisionSequence(list[listIndex]?.revision_number);
  // All-or-nothing: ranking some rounds by number and the rest by position would interleave two
  // orderings and produce an id that agrees with neither screen.
  const oldestFirst = vasIndexes.every((listIndex) => sequenceAt(listIndex) !== null)
    ? [...vasIndexes].sort((a, b) => sequenceAt(a) - sequenceAt(b))
    : [...vasIndexes].reverse();
  // Unlike a "1 of N" phrasing, a lone VAS round still gets an id — "VAS-01" identifies it perfectly
  // well, and suppressing it would mean the customer has nothing to quote on a single-revision order.
  oldestFirst.forEach((listIndex, nthOldest) => {
    ids.set(listIndex, `VAS-${String(nthOldest + 1).padStart(2, "0")}`);
  });
  return ids;
};

/**
 * PGSS-5277: what the revision number reads as, for this viewer.
 *
 * Customers see the VAS id alone — their portal shows only VAS rounds, so the order-wide number is
 * context they have no use for and it is what made REV0005 look like four missing revisions.
 * Everyone else sees the stored number with the id beside it, so a customer quoting "VAS-01" can be
 * matched to REV0005 without opening another screen or counting.
 *
 * Non-VAS rounds always read as their stored number: there is no id to show and quote/drawing rounds
 * are not part of this sequence.
 */
export const revisionDisplayNumber = ({
  storedNumber,
  vasId = null,
  isCustomerView = false,
} = {}) => {
  if (!vasId) return storedNumber;
  return isCustomerView ? vasId : `${storedNumber} (${vasId})`;
};

const REVISION_TYPE_LABELS = {
  quote: "Quote",
  order: "Order",
  drawing: "Drawing",
  proof: "Proof (VAS)",
  vas_price: "VAS price",
  vas_final: "VAS final",
};

const revisionTypeLabel = (type) => {
  if (!type) return null;
  return (
    REVISION_TYPE_LABELS[type] ||
    String(type).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())
  );
};

const RevisionTypePill = ({ type }) => {
  const label = revisionTypeLabel(type);
  if (!label) {
    return (
      <Chip
        size="small"
        label="Unclassified"
        sx={{
          height: 22,
          fontSize: 11,
          fontWeight: 700,
          bgcolor: "#f4f6f9",
          color: "#667085",
          border: "1px solid #e6e9ef",
        }}
        data-testid="revision-type-unclassified"
      />
    );
  }
  const isVas = VAS_REVISION_TYPES.has(type);
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 700,
        bgcolor: isVas ? "#eef2ff" : "#f0f9ff",
        color: isVas ? "#3730a3" : "#075985",
        border: `1px solid ${isVas ? "#c7d2fe" : "#bae6fd"}`,
      }}
      data-testid={`revision-type-${type}`}
    />
  );
};


/**
 * Map one approval round from GET /api/orders/{id}/revisions onto the card's field names.
 * Open rounds OMIT decision keys entirely (not null) — always branch on is_pending.
 */
const roundToCardData = (round) => ({
  revision_number: round?.revision_number,
  revision_type: round?.revision_type,
  approval_status: formatApprovalState(round?.approval_state),
  // PGSS-5266: the raw state as well as the label. DecisionBlock has to tell a superseded round from a
  // decided one, and matching on display text would break the moment a label is reworded.
  approval_state: round?.approval_state,
  is_pending: !!round?.is_pending,
  notes: round?.request_note,
  user_name: round?.requested_by,
  requested_date: round?.requested_at,
  decision_note: round?.decision_note,
  decided_by: round?.decided_by,
  decided_at: round?.decided_at,
  // PGSS-5194: documents attached to the answer. Deliberately NOT merged into doc_groups /
  // added_documents — those describe what was sent, snapshotted when the round opened. Keeping them
  // separate is the whole point: these files used to show up under the next revision's documents.
  decision_documents: round?.decision_documents || [],
  doc_groups: groupDocuments(round?.documents),
  // PGSS-5210: re-attach the binary from `documents` so preview / View / Download work. Only `added`
  // can be resolved this way — a removed document is by definition absent from this round's snapshot,
  // so the API inlines the binary for those (and for decision documents) instead.
  added_documents: withBinaryFrom(round?.documents, round?.added_documents),
  removed_documents: round?.removed_documents || [],
  order_detail_history: (round?.lines || []).map((line) => ({
    productName: line?.product_name,
    subType: line?.sub_type,
    quantity: line?.quantity,
    unitPrice: line?.unit_price,
    weight: line?.weight,
  })),
});

const LineSnapshotTable = ({ rows, type, hidePriceDetails }) => {
  if (!rows?.length) {
    return (
      <Typography className="text-13 text-[#8a93a5]">
        No line snapshot for this revision.
      </Typography>
    );
  }
  return (
    <div className="table-responsive w-full">
      <Box className="w-full">
        <Table size="small" aria-label={`${type}-table`} className="w-full">
          <TableHead className="bg-[#f8f8f8]">
            <TableRow>
              <TableCell className="font-bold text-[#43464b] w-[50px]">
                No
              </TableCell>
              <TableCell className="font-bold text-[#43464b]">
                Product Name
              </TableCell>
              <TableCell className="font-bold text-[#43464b]">Sub Type</TableCell>
              <TableCell className="font-bold text-[#43464b]">Quantity</TableCell>
              <TableCell className="font-bold text-[#43464b]">
                {displayName?.weight}
              </TableCell>
              {!hidePriceDetails && (
                <TableCell className="font-bold text-[#43464b]">
                  Unit Price
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((od, ind) => (
              <TableRow key={`${type}-detail-key-${ind}`}>
                <TableCell>{ind + 1}</TableCell>
                <TableCell>{od?.productName}</TableCell>
                <TableCell>{od?.subType}</TableCell>
                <TableCell>{od?.quantity}</TableCell>
                <TableCell>{od?.weight}</TableCell>
                {!hidePriceDetails && (
                  <TableCell>
                    {/* Rounds API also omits unit_price for no-price staff views. */}
                    {checkNullOrUndefined(od?.unitPrice)
                      ? "—"
                      : formatter.format(od?.unitPrice)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </div>
  );
};

/** Normalize a line row for equality — snapshots have no stable line id across rounds. */
const lineSnapshotSignature = (row) =>
  [
    row?.productName ?? "",
    row?.subType ?? "",
    row?.quantity ?? "",
    row?.weight ?? "",
    row?.unitPrice ?? "",
  ].join("|");

/**
 * Compare this round's line snapshot to the previous (older) round.
 * List is newest-first, so previousLines = items[index + 1].
 * Returns: "changed" | "unchanged" | "unknown" (no prior round to compare).
 */
const lineSnapshotChangeState = (rows, previousRows) => {
  if (previousRows == null) return "unknown";
  const a = (rows || []).map(lineSnapshotSignature);
  const b = (previousRows || []).map(lineSnapshotSignature);
  if (a.length !== b.length) return "changed";
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return "changed";
  }
  return "unchanged";
};

/**
 * Collapsible line snapshot: expand by default when lines changed (or first/unknown);
 * stay collapsed when unchanged vs previous round — click header to peek.
 */
const LineSnapshotSection = ({
  rows,
  previousRows,
  type,
  hidePriceDetails,
}) => {
  const changeState = lineSnapshotChangeState(rows, previousRows);
  const lineCount = rows?.length || 0;
  const [open, setOpen] = useState(changeState !== "unchanged");

  useEffect(() => {
    setOpen(changeState !== "unchanged");
  }, [changeState, lineCount]);

  const badge =
    changeState === "changed"
      ? { label: "Changed", bg: "#e8eefc", fg: "#1d4ed8", border: "#c5d4f5" }
      : changeState === "unchanged"
        ? { label: "Unchanged", bg: "#f4f6f9", fg: "#667085", border: "#e6e9ef" }
        : {
            label: lineCount ? `${lineCount} line${lineCount === 1 ? "" : "s"}` : "Empty",
            bg: "#f4f6f9",
            fg: "#667085",
            border: "#e6e9ef",
          };

  return (
    <div data-testid="revision-line-snapshot">
      <button
        type="button"
        className="flex flex-wrap items-center gap-8 w-full text-left mb-8"
        onClick={() => setOpen((v) => !v)}
        data-testid="revision-line-snapshot-toggle"
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      >
        <Typography className="text-[10.5px] font-extrabold uppercase tracking-wider text-[#8a93a5]">
          Line snapshot
        </Typography>
        <Chip
          size="small"
          label={badge.label}
          sx={{
            height: 20,
            fontSize: 10,
            fontWeight: 700,
            bgcolor: badge.bg,
            color: badge.fg,
            border: `1px solid ${badge.border}`,
          }}
        />
        {changeState === "unchanged" && (
          <Typography className="text-12 text-[#98a2b3]">
            {lineCount} line{lineCount === 1 ? "" : "s"} · same as previous round
          </Typography>
        )}
        <IconButton
          size="small"
          className="ml-auto"
          aria-label={open ? "Collapse line snapshot" : "Expand line snapshot"}
        >
          {open ? (
            <KeyboardArrowUpIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )}
        </IconButton>
      </button>
      <Collapse in={open}>
        <LineSnapshotTable
          rows={rows}
          type={type}
          hidePriceDetails={hidePriceDetails}
        />
      </Collapse>
    </div>
  );
};

const RevisionNavRow = ({ data, index, displayNumber = null }) => (
  <>
    <div className="flex flex-wrap items-center gap-8 mb-4">
      <span className="text-14 font-extrabold text-[#1a2233]">
        Revision {displayNumber ?? data?.revision_number ?? index}
      </span>
      <StatusPill status={roundStatusLabel(data)} />
    </div>
    <div className="text-12 text-[#8a93a5]">
      {formatDateString(data?.requested_date)} · {changeCountLabel(data)}
    </div>
  </>
);

const RevisionCardHeader = ({
  data,
  type,
  index,
  expanded,
  displayNumber = null,
}) => {
  // Test ids stay anchored to the STORED number so a selector does not change with the viewer's role.
  const revisionNo = data?.revision_number ?? index;
  const shownNo = displayNumber ?? revisionNo;
  const typePill =
    type === "rounds" ? (
      <RevisionTypePill type={data?.revision_type} />
    ) : null;
  if (expanded) {
    return (
      <div className="flex flex-wrap items-center gap-10">
        <Typography
          className="text-15 font-extrabold"
          data-testid={`revision-histories-item-${type}-${revisionNo}-number`}
        >
          Revision {shownNo}
        </Typography>
        <StatusPill status={roundStatusLabel(data)} />
        {typePill}
        <Typography className="text-13 text-[#667085]">
          {formatDateString(data?.requested_date)}
        </Typography>
      </div>
    );
  }
  return (
    <>
      <div className="flex flex-wrap items-center gap-8 mb-6">
        <Typography
          className="text-13 font-extrabold text-[#8a93a5]"
          data-testid={`revision-histories-item-${type}-${revisionNo}-number`}
        >
          Rev {shownNo}
        </Typography>
        <StatusPill status={roundStatusLabel(data)} />
        {typePill}
      </div>
      {(() => {
        const preview = collapsedNotesPreview(data);
        return (
          <>
            {preview.kind === "decision" && (
              <Typography className="text-[10.5px] font-extrabold uppercase tracking-wider text-[#a96a14] mb-4">
                Decision note
              </Typography>
            )}
            <Typography
              className={`text-14 font-semibold leading-snug ${
                preview.kind === "empty" ? "text-[#98a2b3]" : "text-red"
              }`}
              data-testid={`revision-histories-item-${type}-${revisionNo}-notes`}
              title={preview.title}
            >
              {preview.text}
            </Typography>
          </>
        );
      })()}
      <Typography className="text-12 text-[#8a93a5] mt-6">
        Requested by{" "}
        <span className="font-bold text-[#475467]">
          {data?.user_name || "—"}
        </span>
        <span className="mx-6">·</span>
        {changeCountLabel(data)}
      </Typography>
    </>
  );
};

/**
 * Document changes on the right, grouped under Proof / Production / … headers.
 * Categories sit side-by-side when space allows.
 */
const DocumentChangesByCategory = ({
  added = [],
  removed = [],
  /** Legacy getRevisionHistory buckets when rounds deltas are unavailable. */
  legacyGroups = null,
  hideSuperseded = true,
}) => {
  // PGSS-5302: Removed is filtered too, reversing the PGSS-5267 decision to exempt it.
  //
  // That decision reasoned that a removed+superseded file still belongs in this bucket, because the
  // round is a point-in-time record of what changed. True, but it loses to the toggle's label: "Hide
  // superseded" is an unqualified promise, and leaving a badged superseded card on screen while it is ON
  // reads as the toggle being broken — which is exactly how it was reported.
  //
  // Accepted consequence: a round whose every document is superseded now shows no document changes at
  // all while the toggle is ON. That is the honest reading of the switch — turn it off to see the full
  // round record, badges and all, which is what the OFF state is for.
  const visibleAdded = documentsForRevisionHistory(added, { hideSuperseded });
  const visibleRemoved = documentsForRevisionHistory(removed, { hideSuperseded });
  const isLegacyMode = Array.isArray(legacyGroups);
  const visibleLegacyGroups = isLegacyMode
    ? legacyGroups
        .map((group) => ({
          ...group,
          docs: documentsForRevisionHistory(group.docs, { hideSuperseded }),
        }))
        .filter((group) => group.docs.length > 0)
    : [];
  const DOC_ORDER = [
    "proof",
    "production",
    "drawing_document",
    "revision",
    "product_proof",
    "other",
  ];

  const byType = (docs) => {
    const map = new Map();
    (docs || []).forEach((doc) => {
      const key = doc?.image_customisation_type || "other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(doc);
    });
    return map;
  };

  const gridClass =
    "mb-16 grid grid-cols-1 md:grid-cols-2 gap-16 items-start";

  // Stay in legacy rendering when legacy buckets were supplied — even if filtering
  // emptied every group — so we never silently fall through to added/removed mode.
  if (isLegacyMode) {
    if (!visibleLegacyGroups.length) {
      return (
        <Typography className="text-13 text-[#8a93a5] mb-14">
          No document changes in this round.
        </Typography>
      );
    }
    return (
      <div className={gridClass} data-testid="revision-document-changes">
        {visibleLegacyGroups.map((group) => (
          <div
            key={group.key}
            className="min-w-0 rounded-lg p-12"
            style={{ border: "1px solid #eef1f5", background: "#fff" }}
          >
            <Typography className="text-[10.5px] mb-8 font-extrabold uppercase tracking-wider text-[#8a93a5]">
              {group.title}
            </Typography>
            <SimpleCardDocumentsViewer
              attachments={group.docs}
              width="w-[13rem]"
              height="h-[10rem]"
              showSupersededBadge
            />
          </div>
        ))}
      </div>
    );
  }

  const addedBy = byType(visibleAdded);
  const removedBy = byType(visibleRemoved);
  const keys = Array.from(
    new Set([...addedBy.keys(), ...removedBy.keys()])
  ).sort((a, b) => {
    const ia = DOC_ORDER.indexOf(a);
    const ib = DOC_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  if (keys.length === 0) {
    return (
      <Typography className="text-13 text-[#8a93a5] mb-14">
        No document changes in this round.
      </Typography>
    );
  }

  return (
    <div className={gridClass} data-testid="revision-document-changes">
      {keys.map((key) => {
        const addedDocs = addedBy.get(key) || [];
        const removedDocs = removedBy.get(key) || [];
        return (
          <div
            key={key}
            className="min-w-0 rounded-lg p-12"
            style={{ border: "1px solid #eef1f5", background: "#fff" }}
          >
            <Typography className="text-[10.5px] mb-8 font-extrabold uppercase tracking-wider text-[#8a93a5]">
              {docGroupTitle(key)}
            </Typography>
            {addedDocs.length > 0 && (
              <div className="mb-10">
                <Typography className="text-12 font-bold text-[#15803d] mb-6">
                  Added ({addedDocs.length})
                </Typography>
                <SimpleCardDocumentsViewer
                  attachments={addedDocs}
                  width="w-[13rem]"
                  height="h-[10rem]"
                  showSupersededBadge
                />
              </div>
            )}
            {removedDocs.length > 0 && (
              <div>
                <Typography className="text-12 font-bold text-[#b0524b] mb-6">
                  Removed ({removedDocs.length})
                </Typography>
                <SimpleCardDocumentsViewer
                  attachments={removedDocs}
                  width="w-[13rem]"
                  height="h-[10rem]"
                  showSupersededBadge
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Does this round have a decision to show?
 *
 * Open rounds omit the decision keys. Closed rounds may have `decided_by` with no note — still show the
 * outcome, so "Decision" is not just a bare name.
 *
 * PGSS-5194: a decision can be nothing but an attachment — a customer requesting a revision who uploads a
 * marked-up drawing and types nothing. Before that the block bailed out and the document had nowhere to
 * appear.
 *
 * PGSS-5286: exported because the answer depends entirely on which feed the round came from, and that is
 * what the defect was. The legacy shape sent none of these keys, so this returned false on every round —
 * an approved VAS revision looked exactly like one nobody had answered.
 */
export const hasDecisionToShow = (data, { hideSuperseded = true } = {}) => {
  if (data?.is_pending) return false;
  const decisionDocs = documentsForRevisionHistory(data?.decision_documents, {
    hideSuperseded,
  });
  return Boolean(
    data?.decision_note || data?.decided_by || decisionDocs.length > 0
  );
};

/**
 * PGSS-5256: a round written before the approval-round columns existed — it records no state, no decision
 * and no decision documents, because none of that was captured when it happened.
 *
 * Such a round is neither awaiting nor decided; it is UNKNOWN, and that third case is what the card had no
 * way to say. It fell through to the decided branch and drew "Decision · Closed" over a name taken from
 * `updated_by` — which on these rows is whoever last wrote them, i.e. the person who *requested* the
 * revision. Order 16238 showed all three of its rounds crediting their own requester with approving them.
 *
 * The backend no longer sends `decided_by` for these (OrderRevisionRoundService), so the check here is the
 * display half of the same rule: say plainly that nothing was recorded rather than render a confident
 * claim, and rather than render nothing at all — "nothing" is what a round nobody answered looks like, and
 * telling those two apart is the whole point of this ticket.
 */
export const isLegacyRound = (data) =>
  !data?.is_pending &&
  // Blank, not merely falsy. The backend decides "legacy" with isBlank, so a whitespace-only state counts
  // as absent there and its decided_by is withheld. Testing truthiness here would disagree: the round
  // would lose the invented name AND miss this notice, leaving the card looking unanswered — the very
  // state this notice exists to rule out. Both endpoints also normalise blank to null; this does not
  // rely on that.
  !String(data?.approval_state ?? "").trim() &&
  !data?.decision_note &&
  !data?.decided_by &&
  !(data?.decision_documents?.length > 0);

/**
 * Which approval rounds this role may see. Shared by the list and the empty-state
 * message so they cannot drift (PGSS-5264 review).
 *
 * - Both product + VAS: everything.
 * - Product only: product types, plus unclassified (pre–PGSS-5041) rows managers still need.
 * - VAS only: typed VAS rounds only — unclassified cannot be classified safely.
 */
export const visibleRoundsFor = ({
  rounds,
  showProductRevision,
  canShowVas,
}) =>
  (rounds || []).filter((round) => {
    if (showProductRevision && canShowVas) return true;
    if (showProductRevision) {
      return (
        !round?.revision_type ||
        PRODUCT_REVISION_TYPES.has(round.revision_type)
      );
    }
    if (canShowVas) {
      return VAS_REVISION_TYPES.has(round?.revision_type);
    }
    return false;
  });

/**
 * Empty-state copy when rounds exist but none are visible for this role.
 * VAS-only roles hide rounds with no revision_type (pre–PGSS-5041). PGSS-5264 retired Classic,
 * so this must not point users at a view that no longer exists.
 */
export const roundsEmptyMessageFor = ({
  rounds,
  showProductRevision,
  canShowVas,
}) => {
  const list = rounds || [];
  if (list.length === 0) return undefined;
  if (
    visibleRoundsFor({ rounds: list, showProductRevision, canShowVas }).length >
    0
  ) {
    return undefined;
  }
  return "Revisions created before this update could not be classified as VAS, so they are not shown here.";
};

const LegacyDecisionNotice = () => (
  <div
    className="px-16 py-12"
    style={{ background: "#f4f6f9", borderBottom: "1px solid #e6e9ef" }}
    data-testid="revision-legacy-no-decision"
  >
    <div className="flex flex-wrap items-baseline gap-x-12 gap-y-4">
      <Typography className="text-[11px] font-extrabold uppercase tracking-wider shrink-0 text-[#667085]">
        No decision recorded
      </Typography>
      <Typography className="text-13 text-[#8a93a5] italic flex-1 min-w-[200px]">
        This revision predates decision history.
      </Typography>
    </div>
  </div>
);

const DecisionBlock = ({ data, hideSuperseded = true }) => {
  const decisionDocs = documentsForRevisionHistory(data?.decision_documents, {
    hideSuperseded,
  });
  // Before hasDecisionToShow: that helper is right that there is no decision here, so it returns false and
  // the block would vanish. A legacy round needs to say why it is empty, not just be empty.
  if (isLegacyRound(data)) return <LegacyDecisionNotice />;
  if (!hasDecisionToShow(data, { hideSuperseded })) return null;
  const banner = closureBanner(data);
  return (
    <div
      className="px-16 py-12"
      style={{
        background: banner.background,
        borderBottom: `1px solid ${banner.border}`,
      }}
      data-testid={banner.testId}
    >
      <div className="flex flex-wrap items-baseline gap-x-12 gap-y-4">
        <Typography
          className={`text-[11px] font-extrabold uppercase tracking-wider shrink-0 ${banner.headingClass}`}
        >
          {banner.heading}
        </Typography>
        {data?.decision_note ? (
          <Typography className="text-13 text-[#344054] leading-relaxed flex-1 min-w-[200px]">
            {data.decision_note}
          </Typography>
        ) : (
          <Typography className="text-13 text-[#8a93a5] italic flex-1">
            No decision note recorded.
          </Typography>
        )}
        {data?.decided_by && (
          <Typography className="text-12 text-[#8a93a5] shrink-0">
            By{" "}
            <span className="font-bold text-[#475467]">{data.decided_by}</span>
            {data?.decided_at ? ` · ${formatDateString(data.decided_at)}` : ""}
          </Typography>
        )}
      </div>
      {decisionDocs.length > 0 && (
        <div className="mt-10" data-testid="revision-decision-documents">
          <Typography className="text-[10.5px] mb-6 font-extrabold uppercase tracking-wider text-[#a96a14]">
            Attached to this decision ({decisionDocs.length})
          </Typography>
          <SimpleCardDocumentsViewer
            attachments={decisionDocs}
            width="w-[13rem]"
            height="h-[10rem]"
            showSupersededBadge
          />
        </div>
      )}
    </div>
  );
};

/**
 * PGSS-5197 card body:
 * - Decision as a full-width outcome strip (not inside "Changes")
 * - Left: request notes
 * - Right: document changes + line snapshot
 */
const RevisionCardBody = ({
  data,
  type,
  index,
  hidePriceDetails,
  previousLines,
  hideSuperseded = true,
}) => {
  const revisionNo = data?.revision_number ?? index;
  const id = revisionKey(type, data, index);

  const legacyDocGroups = type !== "rounds" ? legacyDocumentGroups(data) : null;

  return (
    <div data-testid={`revision-card-${id}`}>
      <DecisionBlock data={data} hideSuperseded={hideSuperseded} />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(240px,28%)_minmax(0,1fr)]">
        <div
          className="px-16 py-14 bg-[#fcfdff]"
          style={{ borderRight: "1px solid #eef1f5" }}
          data-testid={`revision-card-${id}-request`}
        >
          <Typography className="text-[11px] font-extrabold uppercase tracking-wider text-[#4f46e5] mb-10">
            Revision request
          </Typography>
          <Typography className="text-12 text-[#8a93a5] mb-8">
            Requested by{" "}
            <span className="font-bold text-[#475467]">
              {data?.user_name || "—"}
            </span>
          </Typography>
          <div className="mb-4">
            <Typography
              component="span"
              className="text-14 font-bold text-[#43464b] mr-8"
            >
              Notes:
            </Typography>
            <Typography
              component="span"
              className={`text-14 font-semibold ${
                data?.notes ? "text-red" : "text-[#98a2b3]"
              }`}
              data-testid={`revision-histories-item-${type}-${revisionNo}-notes`}
            >
              {data?.notes ||
                (data?.decision_note
                  ? "No send note — see Decision above"
                  : "—")}
            </Typography>
          </div>
        </div>

        <div
          className="px-16 py-14 min-w-0"
          style={{ background: "#f9fbfa" }}
          data-testid={`revision-card-${id}-changes`}
        >
          <Typography className="text-[11px] font-extrabold uppercase tracking-wider text-[#2f6b4f] mb-10">
            Changes this round
          </Typography>

          <Typography className="text-[10.5px] mb-8 font-extrabold uppercase tracking-wider text-[#8a93a5]">
            Documents
          </Typography>
          <DocumentChangesByCategory
            added={data?.added_documents}
            removed={data?.removed_documents}
            legacyGroups={legacyDocGroups}
            hideSuperseded={hideSuperseded}
          />

          <LineSnapshotSection
            rows={data?.order_detail_history}
            previousRows={previousLines}
            type={type}
            hidePriceDetails={hidePriceDetails}
          />
        </div>
      </div>
    </div>
  );
};

const RevisionSplitView = ({
  items,
  type,
  toolbarStart,
  toolbarEnd,
  hidePriceDetails,
  hideSuperseded = true,
  isCustomerView = false,
  emptyMessage = "No record found!",
}) => {
  // PGSS-5277: computed once per render, not per row — the id needs the whole list to know how many
  // VAS rounds there are. The scope comes from `type` because the legacy feed states VAS membership by
  // which bucket a round is in, not on the round itself; see vasRevisionIdsFor.
  const vasIds = vasRevisionIdsFor(items, listScopeForType(type));
  const displayNumberAt = (data, index) =>
    revisionDisplayNumber({
      storedNumber: data?.revision_number ?? index,
      vasId: vasIds.get(index) ?? null,
      isCustomerView,
    });
  return (
  <HistorySplitView
    items={items}
    navHeading="Revisions"
    emptyMessage={emptyMessage}
    showNav={false}
    showNavToggle
    showFocusMode={false}
    toolbarStart={toolbarStart}
    toolbarEnd={toolbarEnd}
    getKey={(data, index) => revisionKey(type, data, index)}
    getInitialExpanded={(data, index) =>
      // PGSS-5286: `is_pending` when the feed sends it — both do now. The fallback reads the round's
      // state label rather than `approval_status`, which on the legacy feed is the order's status: an
      // order parked on "Waiting for VAS Proof Approval" would open every one of its rounds, decided or
      // not. Only rows predating the approval-round columns reach it, and there the status is all there is.
      index === 0 || (data?.is_pending ?? isPendingStatus(roundStatusLabel(data)))
    }
    renderNavRow={(data, index) => (
      <RevisionNavRow
        data={data}
        index={index}
        displayNumber={displayNumberAt(data, index)}
      />
    )}
    renderHeader={(data, index, { expanded }) => (
      <RevisionCardHeader
        data={data}
        type={type}
        index={index}
        expanded={expanded}
        displayNumber={displayNumberAt(data, index)}
      />
    )}
    renderBody={(data, index) => (
      <RevisionCardBody
        data={data}
        type={type}
        index={index}
        hidePriceDetails={hidePriceDetails}
        hideSuperseded={hideSuperseded}
        // Newest-first list: the next index is the prior (older) round for compare.
        previousLines={items[index + 1]?.order_detail_history}
      />
    )}
    testIds={{
      container: `revision-histories-${type}`,
      list: "revision-master-list",
      empty: `revision-histories-${type}-empty`,
      navItem: (id) => `revision-master-item-${id}`,
      card: (id) => `revision-histories-card-${id}`,
      toggle: (id) => `revision-card-${id}-toggle`,
      expandAll: "revision-histories-expand-all",
      collapseAll: "revision-histories-collapse-all",
      focus: "revision-histories-focus-mode",
      navToggle: "revision-histories-list-toggle",
    }}
  />
  );
};

function RevisionHistories(props) {
  const dispatch = useDispatch();
  const { order } = props;
  const hidePriceDetails = isStaffWithoutPriceView(
    (props.user || props.currentUser)?.role_view
  );
  // PGSS-5277: keyed on ROLE, not on which list is rendered. SubmitVas forces the VAS-only list for
  // staff too, so scoping this by list composition would have taken the stored REV number away from
  // staff on VAS pages. Defaulting to false is the safe direction: an unknown role sees the stored
  // number with the id beside it, never the derived id alone.
  const isCustomerView =
    (props.user || props.currentUser)?.role === "customer";
  const showProductRevision = props.showProductRevision ?? true;
  const showVASRevision = props.showVASRevision ?? true;
  const updateRecords = !checkNullOrUndefined(props.updateRecords)
    ? props.updateRecords
    : true;
  const [loading, setLoading] = useState(false);
  const [productRevisionHistories, setProductRevisionHistories] = useState([]);
  const [vasRevisionHistories, setVasRevisionHistories] = useState([]);
  // PGSS-5030 approval rounds: null = endpoint unavailable, use legacy fallback.
  const [rounds, setRounds] = useState(null);
  const [roundsLoading, setRoundsLoading] = useState(true);
  // PGSS-5267: page-session only. Hide superseded on by default; flip off to see
  // the full round record with badges (stamp is live-state, not as-of-round).
  const [hideSuperseded, setHideSuperseded] = useState(true);

  // PGSS-5359: an order that HAD VAS keeps its VAS revisions. This used to read order.vas alone, so
  // deleting the VAS took the segment button away and narrowed visibleRounds to product types — every
  // VAS round the order had became unreachable, while the stored revision numbers carried on counting
  // past them. See vasRevisionVisibility for why live VAS is the wrong question to ask of history.
  const canShowVas = canShowVasRevisions({
    order,
    showVASRevision,
    rounds,
    legacyVasRevisions: vasRevisionHistories,
  });

  const segments = [];
  if (showProductRevision) segments.push("product");
  if (canShowVas) segments.push("vas");

  const [segment, setSegment] = useState(
    showProductRevision ? "product" : "vas"
  );

  useEffect(() => {
    if (!segments.includes(segment)) {
      setSegment(segments[0] || "product");
    }
  }, [showProductRevision, canShowVas, segment]);

  useEffect(() => {
    let active = true;

    // `updateRecords === false` means the caller has already fetched and passed the data in
    // (SubmitVas / ChooseSupplier open this in a dialog that way). Fetching anything then is
    // wasted — and the rounds endpoint is staff-only, so for a customer it is a guaranteed 403
    // on every open. The separate props effect below populates the state instead.
    if (!order?.id || updateRecords !== true) {
      setRoundsLoading(false);
      return () => {
        active = false;
      };
    }

    const load = async () => {
      // Prefer the PGSS-5030 rounds endpoint. Until the backend is deployed the call
      // rejects (404/403) and everything falls back to the legacy shape below.
      setRoundsLoading(true);
      setRounds(null);
      const response = await dispatch(getRevisionRounds(order.id));
      if (!active) return;
      if (!response.error && Array.isArray(response.payload)) {
        // API returns oldest first; the view wants the latest round on top.
        setRounds([...response.payload].reverse().map(roundToCardData));
        setRoundsLoading(false);
        return;
      }

      setRoundsLoading(false);

      setLoading(true);
      const legacyResponse = await dispatch(
        getRevisionHistory({ order_id: order.id })
      );
      if (!active) return;
      if (!legacyResponse.error) {
        const responseData = legacyResponse?.payload;
        if (!checkNullOrUndefined(responseData)) {
          setProductRevisionHistories(responseData?.product || []);
          setVasRevisionHistories(
            newestRevisionFirst([
              ...(responseData?.proof || []),
              ...(responseData?.production || []),
            ])
          );
        }
      }
      setLoading(false);
    };
    load();
    return () => {
      active = false;
    };
  }, [dispatch, order?.id, updateRecords]);

  useEffect(() => {
    if (updateRecords === false) {
      setProductRevisionHistories(props.productRevisionData || []);
    }
  }, [props.productRevisionData, updateRecords]);

  useEffect(() => {
    if (updateRecords === false) {
      // SubmitVas and ChooseSupplier hand this over already concatenated as proof-then-production, so
      // it is ordered here rather than at either call site — one rule, and neither can drift from it.
      setVasRevisionHistories(newestRevisionFirst(props.vasRevisionData || []));
    }
  }, [props.vasRevisionData, updateRecords]);

  const activeItems =
    segment === "vas" ? vasRevisionHistories : productRevisionHistories;

  const segmentControl =
    segments.length > 1 ? (
      <div className="flex gap-8">
        {segments.map((key) => (
          <AuraToolbarButton
            key={key}
            onClick={() => setSegment(key)}
            selected={segment === key}
            dataTestId={`revision-histories-segment-${key}`}
          >
            {key === "product" ? "Product" : "VAS"}
          </AuraToolbarButton>
        ))}
      </div>
    ) : null;

  const supersededToggle = (
    <Tooltip title="On by default. Turn off to see documents that belonged to each round even if they are superseded on the live order (shown with a badge).">
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={hideSuperseded}
            onChange={(event) => setHideSuperseded(event.target.checked)}
            data-testid="revision-histories-hide-superseded"
          />
        }
        label={
          <Typography className="text-12 font-semibold text-[#475467]">
            Hide superseded
          </Typography>
        }
        className="mr-0"
      />
    </Tooltip>
  );

  const toolbarEnd = (
    <div className="flex flex-wrap items-center gap-12">
      {supersededToggle}
    </div>
  );

  const roundsMode = rounds !== null;
  const visibleRounds = visibleRoundsFor({
    rounds,
    showProductRevision,
    canShowVas,
  });
  const roundsEmptyMessage = roundsEmptyMessageFor({
    rounds,
    showProductRevision,
    canShowVas,
  });
  const isLoading = roundsLoading || (!roundsMode && (loading || props.loading));

  return (
    <div data-testid="revision-histories">
      {isLoading && (
        <div
          style={{ height: "50vh" }}
          className="absolute inset-0 bg-white bg-opacity-100 z-10 flex items-center justify-center"
          data-testid="revision-histories-loading"
        >
          <CircularProgress color="inherit" />
        </div>
      )}
      {!isLoading && roundsMode && (
        <>
          {/* PGSS-5030: one flat approval-round list. Product vs VAS is revision_type
              (chip on each card). Missing/wrong revision_type is a backend write-path
              concern (PGSS-5041). */}
          <RevisionSplitView
            items={visibleRounds}
            type="rounds"
            hidePriceDetails={hidePriceDetails}
            hideSuperseded={hideSuperseded}
            isCustomerView={isCustomerView}
            emptyMessage={roundsEmptyMessage}
            toolbarStart={
              <Typography className="text-12 font-bold text-[#8a93a5] uppercase tracking-wider">
                Approval rounds
              </Typography>
            }
            toolbarEnd={toolbarEnd}
          />
        </>
      )}
      {!isLoading && !roundsMode && (
        <>
          {segments.length === 0 && (
            <>
              <div className="flex flex-wrap justify-end items-center gap-12 mb-12">
                {toolbarEnd}
              </div>
              <NoItemMessage
                message="No record found!"
                style={{ message: "text-20" }}
              />
            </>
          )}

          {segments.length > 0 && (
            <RevisionSplitView
              items={activeItems || []}
              type={segment}
              hidePriceDetails={hidePriceDetails}
              hideSuperseded={hideSuperseded}
              isCustomerView={isCustomerView}
              toolbarStart={segmentControl}
              toolbarEnd={toolbarEnd}
            />
          )}
        </>
      )}
    </div>
  );
}

export default RevisionHistories;
